import React, { useMemo, useState } from 'react';
import {
  CondensedHeader,
  Grid,
  GridColumn,
  GridRow,
  useApi,
  useOnDependencyUpdate,
} from '@axiom/ui';
import type { Account, FullCandidateAccount } from '@axiom/validation';
import { CandidateOpportunitiesConst } from '@axiom/const';

import { UserApi } from '../../api/user-api';
import { AccountLegacyApi } from '../../api/account-legacy-api';
import { isEnvoyUser as getIsEnvoyUser } from '../../utils/roles-util';
import { TaxonomyApi } from '../../api/taxonomy-api';

import { MyBenchSortFilters } from './MyBenchSortFilters';
import { MyBenchGrid } from './MyBenchGrid';
import { MyBenchUtil } from './my-bench-util';
import type { MyBenchSortFiltersForm } from './my-bench-sort-filters-form-schema';

export type MyBenchProps = {
  account: Account;
};

export const MyBench = ({ account }: MyBenchProps) => {
  const [{ data: accountCandidates }, { data: taxonomy }, { data: user }] =
    useApi(
      AccountLegacyApi.readAccountCandidates(account.id),
      TaxonomyApi.readTaxonomy(),
      UserApi.readUser()
    );

  const [filters, setFilters] = useState<MyBenchSortFiltersForm | null>(null);
  const isEnvoyUser = getIsEnvoyUser(user);

  const originalCandidatesList = useMemo(() => {
    const allCandidates = MyBenchUtil.addSearchableText({
      candidates: accountCandidates,
      taxonomy,
    });

    if (filters) {
      return MyBenchUtil.filterAndSort({
        filterData: filters,
        candidates: allCandidates,
        isEnvoyUser,
      });
    }

    return allCandidates;
  }, [accountCandidates, taxonomy, filters]);
  const [candidates, setCandidates] = useState<FullCandidateAccount[]>(
    originalCandidatesList
  );

  useOnDependencyUpdate(() => {
    setCandidates(originalCandidatesList);
  }, [originalCandidatesList]);

  const counts = useMemo(() => {
    return candidates.reduce(
      (acc, candidate) => {
        if (
          candidate.accountCandidateStatus ===
          CandidateOpportunitiesConst.CandidateStatuses.Engaged
        ) {
          acc.engagedCount += 1;
        } else if (
          candidate.accountCandidateStatus ===
          CandidateOpportunitiesConst.CandidateStatuses.Completed
        ) {
          acc.completedCount += 1;
        }

        return acc;
      },
      { engagedCount: 0, completedCount: 0 }
    );
  }, [candidates]);

  const handleFilterOnChange = (filterData: MyBenchSortFiltersForm) => {
    const filteredAndSortedCandidates: FullCandidateAccount[] =
      MyBenchUtil.filterAndSort({
        filterData,
        candidates: originalCandidatesList,
        isEnvoyUser,
      });

    setFilters(filterData);
    setCandidates(filteredAndSortedCandidates);
  };

  return (
    <Grid name="MY_BENCH">
      <GridRow gutterBottom="16px">
        <GridColumn>
          <CondensedHeader name="ENGAGED_COUNT_HEADER">
            <span data-test="CURRENTLY_ENGAGED_COUNT">
              Currently engaged: {counts.engagedCount}
            </span>{' '}
            |{' '}
            <span data-test="PREV_ENGAGED_COUNT">
              Previously engaged: {counts.completedCount}
            </span>
          </CondensedHeader>
        </GridColumn>
      </GridRow>
      <GridRow gutterBottom="16px">
        <GridColumn>
          <MyBenchSortFilters
            name="MY_BENCH_SORT_FILTERS"
            isEnvoyUser={isEnvoyUser}
            onChange={handleFilterOnChange}
            taxonomy={taxonomy}
          />
        </GridColumn>
      </GridRow>
      <GridRow>
        <GridColumn>
          <MyBenchGrid
            name="MY_BENCH_GRID"
            candidates={candidates}
            user={user}
            account={account}
            isEnvoyUser={isEnvoyUser}
            taxonomy={taxonomy}
          />
        </GridColumn>
      </GridRow>
    </Grid>
  );
};
